import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FiLock,
  FiEye,
  FiEyeOff,
  FiArrowLeft,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

const glassPanel = {
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 55%, rgba(255,255,255,0.15) 100%)",
  backdropFilter: "blur(22px) saturate(108%)",
  WebkitBackdropFilter: "blur(22px) saturate(108%)",
  border: "1px solid rgba(255,255,255,0.45)",
  boxShadow:
    "0 18px 44px rgba(55,79,99,0.13), inset 0 1px 0 rgba(255,255,255,0.55)",
};

const validatePassword = (password) =>
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password);

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("bibleMemosTheme") || "powder";
    } catch (_e) {
      return "powder";
    }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bibleMemosTheme" && e.newValue) setTheme(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setErrors({});
    setMessage("");
    const nextErrors = {};
    if (!validatePassword(password)) {
      nextErrors.password =
        "At least 8 characters with an uppercase letter, a number, and a special character.";
    }
    if (password !== confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match.";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setStatus("error");
      return;
    }

    setStatus("submitting");
    try {
      const response = await fetch(`/api/auth/reset-password/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setStatus("success");
        setMessage("Password reset. Redirecting you to sign in…");
        setTimeout(() => navigate("/login-redirect"), 1800);
      } else {
        setStatus("error");
        setMessage(data.error || "We couldn't reset your password. The link may have expired.");
      }
    } catch (_e) {
      setStatus("error");
      setMessage("Network error. Please try again in a moment.");
    }
  };

  const fieldWrap =
    "relative flex items-center h-12 w-full rounded-full bg-white/70 border border-white/60 focus-within:bg-white/90 transition-colors";

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors overflow-x-hidden"
    >
      <main className="w-full">
        <div className="w-full max-w-[520px] mx-auto px-margin-mobile py-space-xl flex flex-col gap-space-lg">
          <div
            className="rounded-[1.5rem] p-space-lg md:p-space-xl flex flex-col gap-space-md"
            style={glassPanel}
          >
            <div className="flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                Account · Recovery
              </span>
              <h1 className="font-noto-serif text-headline-lg text-neu-primary">
                Set a new password
              </h1>
              <p className="text-body-md text-on-surface-variant">
                Choose a strong password you haven't used elsewhere.
              </p>
            </div>

            {status === "success" ? (
              <div
                className="rounded-[1rem] p-space-sm flex items-start gap-2 text-body-md text-neu-primary"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0.35))",
                  border: "1px solid rgba(255,255,255,0.45)",
                }}
                aria-live="polite"
              >
                <FiCheckCircle className="text-accent-cta mt-0.5 shrink-0" />
                <span>{message}</span>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-space-md" noValidate>
                <label className="flex flex-col gap-1">
                  <span className="text-label-sm uppercase tracking-wider text-on-surface-variant font-semibold">
                    New password
                  </span>
                  <div className={fieldWrap}>
                    <FiLock
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
                      aria-hidden="true"
                    />
                    <input
                      id="reset-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      placeholder="Enter new password"
                      aria-invalid={Boolean(errors.password)}
                      aria-describedby={errors.password ? "reset-password-error" : undefined}
                      className="w-full h-12 pl-11 pr-11 bg-transparent border-none text-body-md text-neu-primary placeholder:text-on-surface-variant focus:outline-none rounded-full"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-on-surface-variant hover:text-neu-primary rounded-full"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {errors.password && (
                    <p
                      id="reset-password-error"
                      className="text-label-sm text-[#b02a37] mt-1"
                    >
                      {errors.password}
                    </p>
                  )}
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-label-sm uppercase tracking-wider text-on-surface-variant font-semibold">
                    Confirm password
                  </span>
                  <div className={fieldWrap}>
                    <FiLock
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
                      aria-hidden="true"
                    />
                    <input
                      id="reset-confirm"
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      placeholder="Repeat new password"
                      aria-invalid={Boolean(errors.confirmPassword)}
                      aria-describedby={
                        errors.confirmPassword ? "reset-confirm-error" : undefined
                      }
                      className="w-full h-12 pl-11 pr-11 bg-transparent border-none text-body-md text-neu-primary placeholder:text-on-surface-variant focus:outline-none rounded-full"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-on-surface-variant hover:text-neu-primary rounded-full"
                    >
                      {showConfirm ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p
                      id="reset-confirm-error"
                      className="text-label-sm text-[#b02a37] mt-1"
                    >
                      {errors.confirmPassword}
                    </p>
                  )}
                </label>

                {status === "error" && message && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 text-label-md text-[#b02a37]"
                  >
                    <FiAlertCircle className="mt-0.5 shrink-0" />
                    <span>{message}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 min-h-[48px] w-full rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {status === "submitting" ? "Resetting…" : "Reset Password"}
                </button>
              </form>
            )}

            <div className="pt-space-xs">
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-label-md text-accent-cta font-semibold hover:underline min-h-[44px] py-2"
              >
                <FiArrowLeft aria-hidden="true" />
                Return to home
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
